import type { AppRecordValidateResult, NamePath } from '@xbcb/ui-types';
import type { CpscPga, CpscValidationArgs } from './types';
import type { PgaValidator } from 'libs/entryValidation/pga/types';
import { CpscProcessingType } from '__generated__/graphql';
import type { CpscItemFormState } from 'libs/cpsc';
import {
  validateItemsCardinality,
  validateItemType,
  validateIntendedUse,
  mergeValidationResults,
  createValidationResult,
  ValidationResult,
} from './cpscCommonValidation';

/**
 * Validator for CPSC REF (Reference) processing type.
 *
 * CATAIR Requirements:
 * - PG02: items[].itemType (M), items[].productRegistryReference.productId (M), versionId (M)
 * - PG14: items[].productRegistryReference.certifierId (M)
 * - PG01: intendedUseDetails.description (C) - required if code = "980.000"
 *
 * Beta Pilot Constraints:
 * - Max 1 item per PGA Line
 * - itemType must be "PRODUCT"
 */
export class CpscReferenceValidator implements PgaValidator<CpscPga> {
  /**
   * Check if this validator applies to the given data.
   * @param {CpscValidationArgs} args - Validation arguments
   * @return {boolean} true if processingType is REF
   */
  isApplicable({ pga }: CpscValidationArgs): boolean {
    return pga?.processingType === CpscProcessingType.REF;
  }

  /**
   * Validate CPSC REF processing data.
   * @param {CpscValidationArgs} args - Validation arguments
   * @return {AppRecordValidateResult} Validation result with validateFields and additionalErrors
   */
  validate({
    pga,
    tariffNamePath,
    pgaFlag,
  }: CpscValidationArgs): AppRecordValidateResult {
    const cpscPath = [...tariffNamePath, 'pga', pgaFlag];
    const items = pga?.items ?? [];

    // Common validations using shared helpers
    const itemsCardinalityResult = validateItemsCardinality(items, cpscPath);
    const intendedUseResult = validateIntendedUse(pga, cpscPath);

    // Item-level validations
    const itemResults = items.map((item, index) => {
      if (!item) return createValidationResult();
      const itemPath = [...cpscPath, 'items', index];
      const itemTypeResult = validateItemType(item, itemPath);
      const refFieldsResult = this.validateProductRegistryReference(
        item,
        itemPath,
      );
      return mergeValidationResults(itemTypeResult, refFieldsResult);
    });

    // Merge all results
    return mergeValidationResults(
      itemsCardinalityResult,
      intendedUseResult,
      ...itemResults,
    );
  }

  /**
   * Validate product registry reference fields.
   * @param {CpscItemFormState} item - CPSC item form data
   * @param {NamePath} itemPath - Path to item in form
   * @return {ValidationResult} Validation result
   */
  private validateProductRegistryReference(
    item: CpscItemFormState,
    itemPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();
    const productRegistryReference = item.productRegistryReference;

    if (!productRegistryReference?.productId) {
      result.validateFields.push([
        ...itemPath,
        'productRegistryReference',
        'productId',
      ]);
    }

    if (!productRegistryReference?.versionId) {
      result.validateFields.push([
        ...itemPath,
        'productRegistryReference',
        'versionId',
      ]);
    }

    if (!productRegistryReference?.certifierId) {
      result.validateFields.push([
        ...itemPath,
        'productRegistryReference',
        'certifierId',
      ]);
    }

    return result;
  }
}
